/**
 * What the pile amounts to, in the user's words (product spec section 7,
 * "Högen"): how much is prepared for approval, what still needs an answer,
 * what waits on evidence that has not arrived, and whether the bank agrees
 * with the books. Pure over loaded rows; the page composes the sentences.
 *
 * "Done" has three honest readings, and the summary keeps them apart:
 *   all_resolved  every item supplied has a disposition and nothing is owed
 *                 a follow-up;
 *   nothing_now   nothing needs the user now, but bookings still wait for
 *                 their bank row, invoices for their payment, or purchases
 *                 for their receipt;
 *   work_left     something in Att göra needs the user.
 * None of them claims that every business event has been supplied, and none
 * changes a date or locks a period.
 */
import type { Ore } from '../money';
import { formatSek } from '../money';
import { formatSwedishDate } from '../periods/deadlines';
import { bankAccountNumbers } from '../bank/open-items';
import type { OpenItem } from './types';
import type { BankRow, InboxItem, IsoDate, PaymentSource, Verifikation } from '../types';

export interface PileCounts {
  /** Ready to approve as they are. */
  prepared: number;
  /** A question or a check before approval (`check` and `needs_you`), receipts and bank rows alike. */
  questions: number;
  /** Bank rows that are purchases without any document. */
  missingEvidence: number;
  /** Reads that failed; "Läs igen" or fill in by hand. */
  failed: number;
  /** Waiting for AI (no key, a quota) or being read right now. */
  waiting: number;
  /** Items with a disposition: booked, attached, duplicate, private, not a receipt, folded into another. */
  resolved: number;
  /** Bookings whose payment has not shown up on a statement yet. */
  pendingSettlements: number;
  /** Invoices booked and not yet paid. */
  pendingInvoices: number;
  /** Purchases booked without a document that still lack one. */
  missingReceipts: number;
}

export type DoneState = 'all_resolved' | 'nothing_now' | 'work_left';

/** The disposition of a finished item, for the review list. */
export type Disposition = 'booked' | 'attached' | 'duplicate' | 'private' | 'not_receipt' | 'merged' | 'split' | 'skipped';

const ATTACHED_PREFIX = 'Kopplat som kvitto';
const LINKED_PREFIX = 'Kvitto till ett köp som bokfördes utan kvitto';

/** How a terminal item ended, read off its status and the reason it was given. */
export function dispositionOf(item: InboxItem): Disposition | null {
  switch (item.status) {
    case 'booked': {
      const reason = item.tierReasons[0] ?? '';
      return reason.startsWith(ATTACHED_PREFIX) || reason.startsWith(LINKED_PREFIX) ? 'attached' : 'booked';
    }
    case 'private':
      return 'private';
    case 'skipped': {
      if (item.mergedIntoItemId) return 'merged';
      const reason = item.tierReasons[0] ?? '';
      if (reason.startsWith('Dubblett')) return 'duplicate';
      if (reason.startsWith('Delad i')) return 'split';
      if (reason === 'Inte ett kvitto') return 'not_receipt';
      return 'skipped';
    }
    case 'new':
    case 'processing':
    case 'ready':
    case 'check':
    case 'needs_you':
    case 'error':
      return null;
    default: {
      const exhaustive: never = item.status;
      throw new Error(`Unknown status ${String(exhaustive)}`);
    }
  }
}

export interface PileInput {
  items: readonly InboxItem[];
  /** Everything the bookkeeping still expects on a bank account (`deriveOpenItems`). */
  openItems: readonly OpenItem[];
  /** Bookings without a document that still lack one (`missingDocumentIds`). */
  missingReceipts: number;
}

export function pileCounts({ items, openItems, missingReceipts }: PileInput): PileCounts {
  const counts: PileCounts = { prepared: 0, questions: 0, missingEvidence: 0, failed: 0, waiting: 0, resolved: 0, pendingSettlements: 0, pendingInvoices: 0, missingReceipts };
  for (const item of items) {
    switch (item.status) {
      case 'ready':
        counts.prepared++;
        break;
      case 'check':
      case 'needs_you':
        if (item.kind === 'bank_row' && !item.attachmentId && item.proposal?.kind === 'purchase') counts.missingEvidence++;
        else counts.questions++;
        break;
      case 'error':
        counts.failed++;
        break;
      case 'new':
      case 'processing':
        counts.waiting++;
        break;
      case 'booked':
      case 'skipped':
      case 'private':
        counts.resolved++;
        break;
      default: {
        const exhaustive: never = item.status;
        throw new Error(`Unknown status ${String(exhaustive)}`);
      }
    }
  }
  for (const open of openItems) {
    switch (open.kind) {
      case 'settlement':
      case 'reimbursement':
        counts.pendingSettlements++;
        break;
      case 'supplier_invoice':
      case 'customer_invoice':
        counts.pendingInvoices++;
        break;
      default: {
        const exhaustive: never = open.kind;
        throw new Error(`Unknown open item kind ${String(exhaustive)}`);
      }
    }
  }
  return counts;
}

export function doneState(counts: PileCounts): DoneState {
  if (counts.prepared + counts.questions + counts.missingEvidence + counts.failed + counts.waiting > 0) return 'work_left';
  if (counts.pendingSettlements + counts.pendingInvoices + counts.missingReceipts > 0) return 'nothing_now';
  return 'all_resolved';
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** The pile in sentences, one per count that is not zero, in the order the user acts on them. */
export function pileSentences(counts: PileCounts): string[] {
  const out: string[] = [];
  if (counts.prepared) out.push(`${plural(counts.prepared, 'händelse är förberedd', 'händelser är förberedda')} för godkännande.`);
  if (counts.missingEvidence) out.push(`${plural(counts.missingEvidence, 'betalning saknar', 'betalningar saknar')} underlag.`);
  if (counts.questions) out.push(`${plural(counts.questions, 'händelse behöver', 'händelser behöver')} din förklaring.`);
  // "post", not "kvitto": a bank row whose classification failed counts here too.
  if (counts.failed) out.push(`${plural(counts.failed, 'post kunde', 'poster kunde')} inte läsas.`);
  if (counts.waiting) out.push(`${plural(counts.waiting, 'post väntar', 'poster väntar')} på läsning.`);
  if (counts.pendingInvoices) out.push(`${plural(counts.pendingInvoices, 'faktura väntar', 'fakturor väntar')} på betalning.`);
  if (counts.pendingSettlements) out.push(`${plural(counts.pendingSettlements, 'köp väntar', 'köp väntar')} på att synas på kontoutdraget.`);
  if (counts.missingReceipts) out.push(`${plural(counts.missingReceipts, 'köp saknar', 'köp saknar')} kvitto.`);
  return out;
}

/** The headline for the finished pile, per `doneState`. */
export const DONE_TEXT: Record<DoneState, { title: string; body: string }> = {
  all_resolved: { title: 'Allt du lagt in är hanterat.', body: 'Varje kvitto och bankrad är bokförd, kopplad eller undanlagd med ett skäl. Nya kvitton dyker upp här så fort de kommer in.' },
  nothing_now: { title: 'Inget mer behöver dig just nu.', body: 'Det som återstår väntar på något som inte kommit in än. Vi säger till när det gör det.' },
  work_left: { title: 'Här finns saker att göra.', body: '' },
};

// ---- Bank reconciliation ------------------------------------------------------------

export type ReconciliationState = 'ok' | 'differs' | 'unverified';

export interface BankReconciliation {
  sourceId: string;
  sourceName: string;
  state: ReconciliationState;
  /** The statement date the balances are compared on; null when no balance is known. */
  date: IsoDate | null;
  bankOre: Ore | null;
  booksOre: Ore | null;
  /** Rows of the source no booking explains yet. */
  openRows: number;
  /** One Swedish sentence. */
  text: string;
}

/**
 * The latest balance a source's rows report, order-independent: the day's
 * closing balance is the one no other row of that day chains into
 * (balance − amount of some row). When the day does not chain, the highest
 * row id on the latest date is as good a guess as any, and says so.
 */
export function latestBalanceOf(rows: readonly BankRow[]): { date: IsoDate; balanceOre: Ore; certain: boolean } | null {
  const withBalance = rows.filter((r) => r.balanceOre !== undefined);
  if (!withBalance.length) return null;
  const date = withBalance.reduce((m, r) => (r.bookingDate > m ? r.bookingDate : m), withBalance[0]!.bookingDate);
  const day = withBalance.filter((r) => r.bookingDate === date);
  const previous = new Set(day.map((r) => r.balanceOre! - r.amountOre));
  const ends = day.filter((r) => !previous.has(r.balanceOre!));
  if (ends.length === 1) return { date, balanceOre: ends[0]!.balanceOre!, certain: true };
  const fallback = [...day].sort((a, b) => a.id.localeCompare(b.id))[day.length - 1]!;
  return { date, balanceOre: fallback.balanceOre!, certain: day.length === 1 };
}

/** Net of debit − credit on `account` over verifikationer dated on or before `through`. */
export function ledgerBalanceOn(verifikationer: readonly Verifikation[], account: number, through: IsoDate): Ore {
  let net = 0;
  for (const v of verifikationer) {
    if (v.date > through) continue;
    for (const r of v.rows) if (r.accountNumber === account) net += r.debit - r.credit;
  }
  return net;
}

/**
 * Bank against books, per bank source that has statement rows. Verified only
 * when the statement carries a balance; otherwise the honest answer is that
 * it cannot be checked, not that it agrees.
 */
export function bankReconciliation(
  sources: readonly PaymentSource[],
  rows: readonly BankRow[],
  verifikationer: readonly Verifikation[],
  today: IsoDate,
): BankReconciliation[] {
  const out: BankReconciliation[] = [];
  const accounts = bankAccountNumbers(sources);
  for (const source of sources) {
    if (source.kind !== 'bank' || !accounts.has(source.accountNumber)) continue;
    const own = rows.filter((r) => r.paymentSourceId === source.id);
    if (!own.length) continue;
    const openRows = own.filter((r) => r.status === 'open').length;
    const latest = latestBalanceOf(own);
    if (!latest) {
      out.push({
        sourceId: source.id,
        sourceName: source.name,
        state: 'unverified',
        date: null,
        bankOre: null,
        booksOre: null,
        openRows,
        text: `${source.name}: kontoutdraget saknar saldo, så avstämningen mot banken kan inte kontrolleras.`,
      });
      continue;
    }
    const booksOre = ledgerBalanceOn(verifikationer, source.accountNumber, latest.date);
    const when = formatSwedishDate(latest.date, { relativeTo: today });
    const base = `${source.name}: saldo enligt banken ${when} ${formatSek(latest.balanceOre)} kr · enligt bokföringen ${formatSek(booksOre)} kr`;
    if (booksOre === latest.balanceOre) {
      out.push({ sourceId: source.id, sourceName: source.name, state: 'ok', date: latest.date, bankOre: latest.balanceOre, booksOre, openRows, text: `${base} ✓` });
      continue;
    }
    const diff = latest.balanceOre - booksOre;
    const why = openRows > 0 ? ` ${openRows} ${openRows === 1 ? 'rad är inte bokförd' : 'rader är inte bokförda'} än.` : ' Ingen obokförd rad förklarar skillnaden – kontrollera manuella händelser på kontot.';
    out.push({
      sourceId: source.id,
      sourceName: source.name,
      state: 'differs',
      date: latest.date,
      bankOre: latest.balanceOre,
      booksOre,
      openRows,
      text: `${base} · skillnad ${formatSek(diff)} kr.${why}`,
    });
  }
  return out;
}

// ---- Grouped questions ----------------------------------------------------------------

export interface QuestionGroup {
  /** `${field}:${key}:${text}` */
  id: string;
  /** The merchant or bank text the items share. */
  subject: string;
  question: NonNullable<InboxItem['proposal']>['question'] & object;
  items: InboxItem[];
}

/**
 * The same question asked by several items of one counterparty — three
 * receipts from one café without a VAT line, four rows of the same
 * subscription — can be answered once. Grouped only on the same field, the
 * same wording and the same counterparty key, so an answer never propagates
 * to an unrelated purchase; the page still answers each item on its own.
 */
export function groupQuestions(items: readonly InboxItem[], keyOf: (name: string) => string, bankText: (item: InboxItem) => string | undefined): QuestionGroup[] {
  const groups = new Map<string, QuestionGroup>();
  for (const item of items) {
    const question = item.proposal?.question;
    if (!question || item.status === 'booked' || item.status === 'skipped' || item.status === 'private') continue;
    if (question.field === 'match') continue;
    const subject = (item.proposal?.counterparty || item.reading?.merchant || bankText(item) || '').trim();
    const key = keyOf(subject);
    if (!key) continue;
    const id = `${question.field}:${key}:${question.text}`;
    const group = groups.get(id) ?? { id, subject, question, items: [] };
    group.items.push(item);
    groups.set(id, group);
  }
  return [...groups.values()].filter((g) => g.items.length >= 2).sort((a, b) => b.items.length - a.items.length || a.subject.localeCompare(b.subject));
}
